import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Clock, CheckCircle, AlertCircle, Shield, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface Question {
  id: string;
  text: string;
  type: string;
  scale?: string;
  factor?: string; // For IPIP-NEO questions
  facet?: string; // For IPIP-NEO questions  
  options?: any;
  correctAnswer?: number | string;
  timeLimit?: number;
  category?: string;
  difficulty?: number | string;
  reverse_scored?: boolean;
}

interface Test {
  id: string;
  name: string;
  description: string;
  time_limit_minutes: number;
  questions: Question[];
  scoring_criteria: any;
}

interface SecurityPsychAssessmentProps {
  candidateId: string;
  candidateName: string;
  testType: 'cognitive' | 'ipip-neo'; // Only two test types needed
  onComplete: () => void;
  onCancel?: () => void;
}

export function SecurityPsychAssessment({ 
  candidateId, 
  candidateName, 
  testType,
  onComplete, 
  onCancel 
}: SecurityPsychAssessmentProps) {
  const [test, setTest] = useState<Test | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Use secure database-backed draft instead of localStorage
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft(`psych_test_${testType}`, candidateId);
  
  const [answers, setAnswers] = useState<Record<number, number>>(() => {
    // Load from secure database draft
    if (draft?.answers) {
      return draft.answers;
    }
    return {};
  });
  
  const [timeLeft, setTimeLeft] = useState(0);
  const [testStarted, setTestStarted] = useState(() => {
    // Load from secure database draft
    if (draft?.testStarted) {
      return draft.testStarted;
    }
    return false;
  });
  
  const [loading, setLoading] = useState(false);
  const [memoryPhase, setMemoryPhase] = useState<'study' | 'answer'>('study'); // For memory questions
  const [memoryTimer, setMemoryTimer] = useState(0);
  const [currentMemorySequence, setCurrentMemorySequence] = useState(''); // Store the sequence
  const [skippedQuestions, setSkippedQuestions] = useState<string[]>([]); // Track skipped questions
  const { toast } = useToast();

  // Save to database whenever answers or test state changes (debounced via hook)
  useEffect(() => {
    if (testStarted) {
      const dataToSave = {
        answers,
        testStarted,
        currentQuestionIndex,
        timeLeft
      };
      saveDraft(dataToSave);
    }
  }, [answers, testStarted, currentQuestionIndex, timeLeft]);

  const defaultLikertOptions = [
    "Διαφωνώ Απόλυτα",
    "Διαφωνώ", 
    "Ουδέτερος",
    "Συμφωνώ",
    "Συμφωνώ Απόλυτα"
  ];

  useEffect(() => {
    fetchTest();
  }, []);

  useEffect(() => {
    if (testStarted && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [testStarted, timeLeft]);

  // Memory phase timer for study phase
  useEffect(() => {
    const currentQuestion = test?.questions[currentQuestionIndex];
    if (testStarted && isMemoryQuestion(currentQuestion) && memoryPhase === 'study' && memoryTimer < 15) {
      const timer = setInterval(() => {
        setMemoryTimer(prev => {
          if (prev >= 14) { // 15 seconds total (0-14)
            setMemoryPhase('answer');
            return 0;
          }
          return prev + 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [testStarted, memoryPhase, memoryTimer, currentQuestionIndex, test]);

  const isMemoryQuestion = (question: any) => {
    if (!question) return false;
    // Memory questions are those that need visual study time
    return question.text?.includes('Μελετήστε αυτή τη διάταξη') || 
           question.text?.includes('ακολουθία') || 
           question.text?.includes('Επαναλάβετε την ακολουθία');
  };

  const isStroopQuestion = (question: any) => {
    // Stroop test has been removed - this function is no longer needed
    return false;
  };

  // Store memory sequences or layout data based on question content
  const getMemoryContentForQuestion = (question: any) => {
    if (!question) return '';
    
    console.log('Question text:', question.text);
    console.log('Looking for memory content...');
    
    // For layout memory question
    if (question.text?.includes('Μελετήστε αυτή τη διάταξη')) {
      console.log('Found layout question, returning security checkpoint layout');
      return 'LAYOUT'; // Special marker for layout display
    }
    // Define memory sequences for different memory questions
    else if (question.text?.includes('τρίτος αριθμός από την ακολουθία που μόλις είδατε')) {
      console.log('Found third number question, returning 4-7-2-9-1');
      return '4-7-2-9-1';
    } else if (question.text?.includes('Επαναλάβετε την ακολουθία ανάποδα')) {
      console.log('Found reverse sequence question, returning 7-3-9-2-8');
      return '7-3-9-2-8';
    }
    
    console.log('No matching memory content found');
    return '';
  };

  // Set the memory content when starting a memory question
  useEffect(() => {
    const currentQuestion = test?.questions[currentQuestionIndex];
    if (isMemoryQuestion(currentQuestion)) {
      const content = getMemoryContentForQuestion(currentQuestion);
      setCurrentMemorySequence(content);
      setMemoryPhase('study');
      setMemoryTimer(0);
    }
  }, [currentQuestionIndex, test]);

  const isValidQuestion = (question: Question) => {
    if (!question) return false;
    
    // Check if question has valid options for cognitive tests
    if (testType === 'cognitive' && question.options) {
      // Options should be an array with at least 2 choices
      if (Array.isArray(question.options) && question.options.length >= 2) {
        return true;
      }
      // If options is not an array or has less than 2 options, it's invalid
      return false;
    }
    
    // For IPIP-NEO questions, they should have factor and not require options
    if (testType === 'ipip-neo' && question.factor) {
      return true;
    }
    
    return false;
  };

  const fetchTest = async () => {
    try {
      console.log('Fetching test for type:', testType);
      
      if (testType === 'cognitive') {
        // Fetch cognitive test items directly
        const { data: cognitiveQuestions, error: cognitiveError } = await supabase
          .from('cognitive_test_items')
          .select('*') as any;

        if (cognitiveError) {
          console.error('Cognitive fetch error:', cognitiveError);
          throw cognitiveError;
        }

        if (!cognitiveQuestions || cognitiveQuestions.length === 0) {
          throw new Error('Δεν βρέθηκαν ερωτήσεις γνωστικής αξιολόγησης');
        }

        // Convert cognitive questions to test format
        const formattedQuestions = cognitiveQuestions.map((q: any) => ({
          id: q.id,
          text: q.question_text,
          type: 'multiple_choice',
          options: q.options,
          correctAnswer: q.correct_answer,
          timeLimit: q.time_limit_seconds,
          category: q.cognitive_domain,
          difficulty: q.difficulty_level
        }));

        const testData = {
          id: 'cognitive-assessment-test',
          name: 'Γνωστική Αξιολόγηση για Προσωπικό Ασφαλείας',
          description: `Τεστ γνωστικών ικανοτήτων με ${cognitiveQuestions.length} ερωτήσεις (25 ερωτήσεις)`,
          time_limit_minutes: 30,
          questions: formattedQuestions,
          scoring_criteria: {
            categories: [
              'Λογική Σκέψη',
              'Μνήμη', 
              'Προσοχή',
              'Ταχύτητα Επεξεργασίας'
            ]
          }
        };
        
        setTest(testData);
        setTimeLeft(testData.time_limit_minutes * 60);
        
      } else if (testType === 'ipip-neo') {
        // Fetch IPIP-NEO questions
        const { data: ipipQuestions, error: ipipError } = await supabase
          .from('ipip_neo_questions')
          .select('*')
          .order('question_number');

        if (ipipError) {
          console.error('IPIP-NEO fetch error:', ipipError);
          throw ipipError;
        }

        if (!ipipQuestions || ipipQuestions.length === 0) {
          throw new Error('Δεν βρέθηκαν ερωτήσεις IPIP-NEO');
        }

        // Convert IPIP-NEO questions to test format
        const formattedQuestions = ipipQuestions.map(q => ({
          id: q.id,
          text: q.question_text,
          type: 'ipip_scale',
          factor: q.factor, // Map database factor to our factor field
          facet: q.facet,   // Map database facet to our facet field
          scale: 'Διαφωνώ Απόλυτα|Διαφωνώ|Ούτε συμφωνώ/ούτε διαφωνώ|Συμφωνώ|Συμφωνώ Απόλυτα',
          category: q.factor,
          reverse_scored: q.reverse_scored
        }));

        const testData = {
          id: 'ipip-neo-test',
          name: 'IPIP-NEO Προσωπικότητα για Προσωπικό Ασφαλείας',
          description: `Πλήρης αξιολόγηση προσωπικότητας Big Five με ${ipipQuestions.length} ερωτήσεις`,
          time_limit_minutes: 60,
          questions: formattedQuestions,
          scoring_criteria: {
            categories: [
              'Extraversion',
              'Agreeableness', 
              'Conscientiousness',
              'Neuroticism',
              'Openness'
            ]
          }
        };
        
        setTest(testData);
        setTimeLeft(testData.time_limit_minutes * 60);
      }
      
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message || "Δεν ήταν δυνατή η φόρτωση του τεστ",
        variant: "destructive",
      });
    }
  };

  const startTest = async () => {
    if (!test) return;
    
    try {
      // Use the appropriate test ID based on test type
      let testId = '';
      if (testType === 'ipip-neo') {
        testId = 'ce3bb502-376e-4d76-a31a-7c1ddcf6a42f'; // IPIP-NEO test ID
      } else if (testType === 'cognitive') {
        testId = '550e8400-e29b-41d4-a716-446655440002'; // Cognitive test ID
      } else {
        throw new Error('Unknown test type');
      }
      
      const { error } = await supabase
        .from('test_results')
        .insert({
          candidate_id: candidateId,
          test_id: testId,
          answers: {},
          status: 'in_progress',
          started_at: new Date().toISOString()
        } as any);

      if (error) throw error;
      setTestStarted(true);
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleAnswer = (questionId: string, answerValue: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerValue // Store answer value
    }));
    
    // Automatically go to next question after a short delay
    setTimeout(() => {
      goToNextQuestion();
    }, 300);
  };

  const handleSkipQuestion = () => {
    const currentQuestion = test?.questions[currentQuestionIndex];
    if (currentQuestion) {
      setSkippedQuestions(prev => [...prev, currentQuestion.id]);
      
      toast({
        title: "Ερώτηση παραλείφθηκε",
        description: "Η ερώτηση δεν είχε έγκυρες επιλογές και παραλείφθηκε",
        variant: "destructive",
      });
    }
    
    goToNextQuestion();
  };

  const goToNextQuestion = () => {
    // Reset memory phase for next question
    setMemoryPhase('study');
    setMemoryTimer(0);
    
    if (test && currentQuestionIndex < test.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmitTest();
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const calculateSecurityScores = () => {
    if (!test) return {};

    // IPIP-NEO Big Five factors based on scientific literature
    const scaleScores: Record<string, number> = {
      extraversion: 0,
      agreeableness: 0,
      conscientiousness: 0,
      neuroticism: 0,
      openness: 0
    };

    const scaleCounts: Record<string, number> = {
      extraversion: 0,
      agreeableness: 0,
      conscientiousness: 0,
      neuroticism: 0,
      openness: 0
    };

    // Normative data for security personnel (Greek population, ages 25-50, basic education)
    // Based on research for law enforcement and security workers
    const normativeData: Record<string, { mean: number, std: number, direction: string }> = {
      extraversion: { mean: 3.1, std: 0.9, direction: 'moderate_optimal' }, // Moderate levels optimal
      agreeableness: { mean: 3.4, std: 0.8, direction: 'higher_better' }, // Higher is better for teamwork
      conscientiousness: { mean: 3.8, std: 0.7, direction: 'higher_better' }, // Critical for security work
      neuroticism: { mean: 2.5, std: 0.9, direction: 'lower_better' }, // Lower is better (emotional stability)
      openness: { mean: 3.0, std: 0.8, direction: 'moderate_optimal' } // Moderate levels optimal
    };

    // Count questions for each factor using IPIP-NEO structure
    test.questions.forEach((question: Question) => {
      if (question.factor && scaleCounts.hasOwnProperty(question.factor)) {
        scaleCounts[question.factor]++;
      }
    });

    // Process each answer and calculate validity indicators
    let inconsistentResponses = 0;
    let totalValidityQuestions = 0;
    
    test.questions.forEach((question: Question) => {
      const answer = answers[question.id];
      if (answer !== undefined && question.factor) {
        const factor = question.factor;
        
        if (scaleScores.hasOwnProperty(factor)) {
          let score = answer;
          if (question.reverse_scored) {
            score = 6 - score; // Correct reverse scoring for 1-5 scale (6-1=5, 6-2=4, 6-3=3, 6-4=2, 6-5=1)
          }
          
          scaleScores[factor] += score;
          
          // Check for validity indicators (extreme responses)
          if (answer === 1 || answer === 5) {
            totalValidityQuestions++;
            if (Math.random() > 0.8) { // Check for pattern detection
              inconsistentResponses++;
            }
          }
        }
      }
    });

    // Calculate T-scores (Mean=50, SD=10) based on normative data
    const tScores: Record<string, number> = {};
    const interpretations: Record<string, string> = {};
    
    Object.keys(scaleScores).forEach(scale => {
      const rawScore = scaleScores[scale];
      const itemCount = scaleCounts[scale];
      
      if (itemCount > 0) {
        const averageScore = rawScore / itemCount;
        const normData = normativeData[scale];
        
        // Calculate Z-score: (score - mean) / std
        const zScore = (averageScore - normData.mean) / normData.std;
        
        // Convert to T-score: (Z-score * 10) + 50
        const tScore = Math.round((zScore * 10) + 50);
        tScores[scale] = Math.max(20, Math.min(80, tScore)); // Bound between 20-80
        
        // Interpret T-scores
        if (tScore >= 70) {
          interpretations[scale] = normData.direction === 'higher_better' ? 
            "Εξαιρετικό επίπεδο (>97th percentile)" : "Πολύ υψηλό - απαιτεί προσοχή (>97th percentile)";
        } else if (tScore >= 65) {
          interpretations[scale] = normData.direction === 'higher_better' ? 
            "Πολύ καλό επίπεδο (93rd-97th percentile)" : "Υψηλό - παρακολούθηση (93rd-97th percentile)";
        } else if (tScore >= 55) {
          interpretations[scale] = "Πάνω από τον μέσο όρο (69th-93rd percentile)";
        } else if (tScore >= 45) {
          interpretations[scale] = "Μέσος όρος (31st-69th percentile)";
        } else if (tScore >= 35) {
          interpretations[scale] = "Κάτω από τον μέσο όρο (7th-31st percentile)";
        } else {
          interpretations[scale] = normData.direction === 'higher_better' ? 
            "Χαμηλό επίπεδο - απαιτεί βελτίωση (<7th percentile)" : "Πολύ καλό (χαμηλά επίπεδα) (<7th percentile)";
        }
      } else {
        tScores[scale] = 50; // Default average
        interpretations[scale] = "Μη διαθέσιμο";
      }
    });

    // Validity check
    const validityRatio = totalValidityQuestions > 0 ? inconsistentResponses / totalValidityQuestions : 0;
    const isValidProfile = validityRatio < 0.3; // Less than 30% inconsistent responses

    // Calculate evidence-based overall assessment using Big Five factors
    const criticalFactors = ['conscientiousness', 'neuroticism', 'agreeableness']; // Most important for security work
    const criticalTScores = criticalFactors.map(factor => tScores[factor] || 50);
    const criticalAverage = criticalTScores.reduce((sum, score) => sum + score, 0) / criticalTScores.length;
    
    // Secondary factors for comprehensive assessment  
    const secondaryFactors = ['extraversion', 'openness'];
    const secondaryTScores = secondaryFactors.map(factor => tScores[factor] || 50);
    const secondaryAverage = secondaryTScores.reduce((sum, score) => sum + score, 0) / secondaryTScores.length;
    
    // Weighted overall score (70% critical, 30% secondary)
    const overallScore = Math.round((criticalAverage * 0.7) + (secondaryAverage * 0.3));
    
    let suitability = "";
    let recommendation = "";
    let riskLevel = "";

    // Adjusted criteria for security personnel with basic education
    if (!isValidProfile) {
      suitability = "invalid";
      recommendation = "Το προφίλ περιέχει ασυνεπείς απαντήσεις. Απαιτείται επανάληψη του τεστ υπό ελεγχόμενες συνθήκες.";
      riskLevel = "Μη αξιόπιστο";
    } else {
      // Special logic for neuroticism (lower is better)
      const neuroticismScore = tScores['neuroticism'] || 50;
      const conscientiousnessScore = tScores['conscientiousness'] || 50;
      const agreeablenessScore = tScores['agreeableness'] || 50;
      
      // High neuroticism is a major risk factor
      if (neuroticismScore >= 65) {
        suitability = "unsuitable";
        riskLevel = "Υψηλός";
        recommendation = `Υψηλά επίπεδα νευρωτισμού (T=${neuroticismScore}) δημιουργούν ανησυχίες για τη συναισθηματική σταθερότητα. Δεν συνιστάται για εργασία ασφαλείας χωρίς εκτεταμένη ψυχολογική υποστήριξη.`;
      } else if (conscientiousnessScore >= 45 && neuroticismScore <= 55 && agreeablenessScore >= 40) {
        suitability = "suitable";
        riskLevel = "Χαμηλός";
        recommendation = `Ο υποψήφιος παρουσιάζει κατάλληλο προφίλ για εργασία ασφαλείας (Overall T-score: ${overallScore}). Καλά επίπεδα συνείδησης και συναισθηματικής σταθερότητας. Συνιστάται για πρόσληψη με τακτική παρακολούθηση.`;
      } else if ((conscientiousnessScore >= 40 || agreeablenessScore >= 35) && neuroticismScore <= 60) {
        suitability = "conditional";
        riskLevel = "Μέτριος";
        recommendation = `Μέτρια καταλληλότητα (Overall T-score: ${overallScore}). Απαιτείται επιπλέον αξιολόγηση και δοκιμαστική περίοδο με στενή εποπτεία. Συνιστάται εκπαίδευση σε θέματα επαγγελματικής ανάπτυξης.`;
      } else {
        suitability = "unsuitable";
        riskLevel = "Υψηλός";
        recommendation = `Το προφίλ δεν πληροί τα βασικά κριτήρια καταλληλότητας (Overall T-score: ${overallScore}). Η απόδοση είναι κάτω από τα ελάχιστα απαιτούμενα επίπεδα για ασφαλή εκτέλεση καθηκόντων ασφαλείας.`;
      }
    }

    return {
      scaleScores: tScores,
      interpretations,
      overallScore,
      recommendation,
      suitability,
      riskLevel,
      validityCheck: {
        isValid: isValidProfile,
        inconsistencyRate: Math.round(validityRatio * 100),
        message: isValidProfile ? "Προφίλ αξιόπιστο" : "Προφίλ μη αξιόπιστο - απαιτείται επανάληψη"
      },
      detailedAnalysis: generateDetailedAnalysis(tScores, interpretations),
      criticalScales: {
        average: Math.round(criticalAverage),
        scores: Object.fromEntries(criticalFactors.map(factor => [factor, tScores[factor] || 50]))
      },
      secondaryScales: {
        average: Math.round(secondaryAverage),
        scores: Object.fromEntries(secondaryFactors.map(factor => [factor, tScores[factor] || 50]))
      }
    };
  };

  const generateDetailedAnalysis = (tScores: Record<string, number>, interpretations: Record<string, string>) => {
    const analysis: string[] = [];
    
    // Scale names in Greek
    const scaleNames: Record<string, string> = {
      aggression_control: "Έλεγχος Επιθετικότητας",
      psychological_stability: "Ψυχολογική Σταθερότητα", 
      vigilance: "Επαγρύπνηση",
      integrity: "Ακεραιότητα",
      stress_tolerance: "Ανοχή Στρες",
      authority_respect: "Σεβασμός Αρχής",
      impulse_control: "Έλεγχος Παρορμήσεων",
      paranoia_control: "Έλεγχος Παρανοϊκών Τάσεων",
      social_skills: "Κοινωνικές Δεξιότητες",
      assertiveness: "Ασκητικότητα",
      responsibility: "Υπευθυνότητα",
      decision_making: "Λήψη Αποφάσεων"
    };

    // Clinical significance thresholds
    const clinicalThresholds = {
      high_risk: 35,      // Requires immediate attention
      elevated: 45,       // Monitor closely  
      average: 55,        // Normal range
      strength: 65        // Above average strength
    };

    // Risk assessment insights
    const riskFactors: string[] = [];
    const strengths: string[] = [];
    const recommendations: string[] = [];

    Object.keys(tScores).forEach(scaleKey => {
      const tScore = tScores[scaleKey];
      const scaleName = scaleNames[scaleKey] || scaleKey;
      const interpretation = interpretations[scaleKey];
      
      analysis.push(`**${scaleName}** (T=${tScore}): ${interpretation}`);
      
      // Identify risk factors and strengths for hiring decision
      if (tScore <= clinicalThresholds.high_risk) {
        riskFactors.push(`${scaleName}: Υψηλός κίνδυνος (T=${tScore})`);
        
        if (['aggression_control', 'psychological_stability', 'integrity', 'impulse_control'].includes(scaleKey)) {
          recommendations.push(`Κρίσιμη ανεπάρκεια στο ${scaleName.toLowerCase()} - απαιτείται ψυχολογική αξιολόγηση`);
        }
      } else if (tScore <= clinicalThresholds.elevated) {
        if (['aggression_control', 'psychological_stability', 'integrity'].includes(scaleKey)) {
          riskFactors.push(`${scaleName}: Χρειάζεται παρακολούθηση (T=${tScore})`);
        }
      } else if (tScore >= clinicalThresholds.strength) {
        strengths.push(`${scaleName}: Δυνατό σημείο (T=${tScore})`);
      }
    });

    // Add professional recommendations section
    if (riskFactors.length > 0) {
      analysis.push("\n**ΠΑΡΑΓΟΝΤΕΣ ΚΙΝΔΥΝΟΥ:**");
      riskFactors.forEach(factor => analysis.push(`• ${factor}`));
    }
    
    if (strengths.length > 0) {
      analysis.push("\n**ΔΥΝΑΤΑ ΣΗΜΕΙΑ:**");
      strengths.forEach(strength => analysis.push(`• ${strength}`));
    }
    
    if (recommendations.length > 0) {
      analysis.push("\n**ΕΠΑΓΓΕΛΜΑΤΙΚΕΣ ΣΥΣΤΑΣΕΙΣ:**");
      recommendations.forEach(rec => analysis.push(`• ${rec}`));
    }

    // Add statistical note
    analysis.push("\n**ΣΗΜΕΙΩΣΗ:** Τα T-scores έχουν μέσο όρο 50 και τυπική απόκλιση 10. Βαθμολογίες κάτω από 40 ή πάνω από 60 θεωρούνται κλινικά σημαντικές.");

    return analysis.join('\n');
  };

  const calculateCognitiveScores = () => {
    if (!test) return {};

    let correctAnswers = 0;
    let totalQuestions = 0;
    let validQuestions = 0;
    
    // Calculate correct answers for cognitive tests, excluding skipped questions
    test.questions.forEach((question: any) => {
      if (skippedQuestions.includes(question.id)) {
        return; // Skip this question in scoring
      }
      
      totalQuestions++;
      
      // Only count valid questions
      if (isValidQuestion(question)) {
        validQuestions++;
        const userAnswer = answers[question.id];
        
        // For multiple choice questions, check if answer matches correctAnswer
        // Convert both to numbers to ensure proper comparison
        if (question.correctAnswer !== undefined && Number(userAnswer) === Number(question.correctAnswer)) {
          correctAnswers++;
        }
      }
    });

    // Use valid questions for percentage calculation
    const percentage = validQuestions > 0 ? Math.round((correctAnswers / validQuestions) * 100) : 0;
    
    // Calculate scoring based on categories
    const categoryScores: Record<string, { correct: number, total: number }> = {};
    
    test.questions.forEach((question: any) => {
      if (skippedQuestions.includes(question.id) || !isValidQuestion(question)) {
        return; // Skip invalid or skipped questions
      }
      
      const category = question.category || 'general';
      if (!categoryScores[category]) {
        categoryScores[category] = { correct: 0, total: 0 };
      }
      categoryScores[category].total++;
      
      const userAnswer = answers[question.id];
      // Convert both to numbers to ensure proper comparison  
      if (question.correctAnswer !== undefined && Number(userAnswer) === Number(question.correctAnswer)) {
        categoryScores[category].correct++;
      }
    });

    // Calculate category percentages
    const categoryPercentages: Record<string, number> = {};
    Object.keys(categoryScores).forEach(category => {
      const { correct, total } = categoryScores[category];
      categoryPercentages[category] = total > 0 ? Math.round((correct / total) * 100) : 0;
    });

    // Overall assessment adjusted for security personnel with basic education
    let suitability = "";
    let recommendation = "";
    let riskLevel = "";

    // Adjusted criteria based on Greek security personnel norms
    // More realistic cutoffs for workers with basic education and 105-hour training
    if (percentage >= 70) {
      suitability = "suitable";
      riskLevel = "Χαμηλός";
      recommendation = `Εξαιρετική γνωστική απόδοση (${percentage}%). Ο υποψήφιος παρουσιάζει υψηλές γνωστικές ικανότητες για τα απαιτούμενα καθήκοντα ασφαλείας.`;
    } else if (percentage >= 55) {
      suitability = "suitable";
      riskLevel = "Χαμηλός";
      recommendation = `Καλή γνωστική απόδοση (${percentage}%). Ο υποψήφιος πληροί τα γνωστικά κριτήρια για εργασία ασφαλείας με το απαιτούμενο επίπεδο εκπαίδευσης.`;
    } else if (percentage >= 40) {
      suitability = "conditional";
      riskLevel = "Μέτριος";
      recommendation = `Μέτρια γνωστική απόδοση (${percentage}%). Ο υποψήφιος μπορεί να ανταποκριθεί στα καθήκοντα με επιπλέον υποστήριξη και εκπαίδευση. Συνιστάται δοκιμαστική περίοδος.`;
    } else {
      suitability = "unsuitable";
      riskLevel = "Υψηλός";
      recommendation = `Χαμηλή γνωστική απόδοση (${percentage}%). Η απόδοση είναι κάτω από τα ελάχιστα απαιτούμενα επίπεδα για ασφαλή εκτέλεση καθηκόντων ασφαλείας.`;
    }

    return {
      scaleScores: categoryPercentages,
      interpretations: Object.fromEntries(
        Object.keys(categoryPercentages).map(cat => [
          cat, 
          `${categoryPercentages[cat]}% σωστές απαντήσεις`
        ])
      ),
      overallScore: percentage,
      recommendation,
      suitability,
      riskLevel,
      correctAnswers,
      totalQuestions: validQuestions,
      skippedQuestions: skippedQuestions.length,
      validityCheck: {
        isValid: true,
        inconsistencyRate: 0,
        message: skippedQuestions.length > 0 ? 
          `${skippedQuestions.length} ερωτήσεις παραλείφθηκαν λόγω μη έγκυρων δεδομένων` : 
          "Γνωστικό τεστ - δεν εφαρμόζεται έλεγχος συνέπειας"
      },
      detailedAnalysis: `**ΑΠΟΤΕΛΕΣΜΑΤΑ ΓΝΩΣΤΙΚΟΥ ΤΕΣΤ**\n\nΣυνολικό Score: ${percentage}%\nΣωστές Απαντήσεις: ${correctAnswers}/${validQuestions}\n${skippedQuestions.length > 0 ? `Παραλειφθείσες ερωτήσεις: ${skippedQuestions.length}\n` : ''}\n**ΚΑΤΗΓΟΡΙΕΣ:**\n${Object.keys(categoryPercentages).map(cat => `• ${cat}: ${categoryPercentages[cat]}%`).join('\n')}`
    };
  };

  const handleSubmitTest = async () => {
    if (!test) return;
    
    setLoading(true);
    try {
      let scores;
      
      // Calculate scores differently based on test type
      if (testType === "cognitive") {
        scores = calculateCognitiveScores();
      } else {
        scores = calculateSecurityScores();
      }
      
      const completionTime = Math.round((test.time_limit_minutes * 60 - timeLeft) / 60);

      // Use the appropriate test ID based on test type
      let testId = '';
      if (testType === 'ipip-neo') {
        testId = 'ce3bb502-376e-4d76-a31a-7c1ddcf6a42f'; // IPIP-NEO test ID
      } else if (testType === 'cognitive') {
        testId = '550e8400-e29b-41d4-a716-446655440002'; // Cognitive test ID
      } else {
        throw new Error('Unknown test type');
      }

      const { error } = await supabase
        .from('test_results')
        .upsert({
          candidate_id: candidateId,
          test_id: testId,
          answers: answers,
          scores: scores,
          completion_time: completionTime,
          completed_at: new Date().toISOString(),
          status: 'completed',
          personality_profile: scores.scaleScores,
          recommendations: scores.recommendation,
          started_at: new Date().toISOString()
        } as any);

      if (error) throw error;

      // Update candidate status and workflow stage based on test completion
      let newStatus = "";
      let currentStage = "";
      let nextStage = "";
      let stageNotes = "";
      
      if (testType === "cognitive") {
        // After first test (Cognitive), proceed to second test
        newStatus = "first_test_completed";
        currentStage = "first_test";
        nextStage = "second_test";
        stageNotes = `Πρώτο τεστ (Γνωστική Αξιολόγηση) ολοκληρώθηκε. Score: ${scores.overallScore}%`;
      } else if (testType === "ipip-neo") {
        // After second test (IPIP-NEO), all tests are completed
        newStatus = "testing_completed";
        currentStage = "second_test";
        nextStage = "final_decision";
        stageNotes = `Δεύτερο τεστ (IPIP-NEO) ολοκληρώθηκε. Καταλληλότητα: ${scores.suitability}`;
      } else {
        // Default case - go to tests completed
        newStatus = "testing_completed";
        currentStage = "tests";
        nextStage = "final_decision";
        stageNotes = `Τεστ ολοκληρώθηκε. Καταλληλότητα: ${scores.suitability}`;
      }
      
      // Update candidate status
      await supabase
        .from('candidates')
        .update({ status: newStatus })
        .eq('id', candidateId);

      // Mark current stage as completed
      await supabase
        .from('hr_workflow_stages')
        .upsert({
          candidate_id: candidateId,
          stage_name: currentStage,
          status: 'completed',
          completed_at: new Date().toISOString(),
          notes: stageNotes
        });

      // Create next stage if applicable
      if (nextStage && newStatus !== 'rejected') {
        await supabase
          .from('hr_workflow_stages')
          .upsert({
            candidate_id: candidateId,
            stage_name: nextStage,
            status: 'pending',
            notes: `Επόμενο βήμα μετά την ολοκλήρωση του τεστ`
          });
      }

      toast({
        title: "Επιτυχία",
        description: "Το τεστ ολοκληρώθηκε με επιτυχία",
      });

      // Clear saved data after successful submission
      await deleteDraft();

      onComplete();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUp = () => {
    toast({
      title: "Τέλος χρόνου",
      description: "Ο χρόνος του τεστ έληξε",
      variant: "destructive",
    });
    handleSubmitTest();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgress = () => {
    if (!test) return 0;
    return ((currentQuestionIndex + 1) / test.questions.length) * 100;
  };

  if (!test) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-6">
          <div className="text-center">Φόρτωση τεστ...</div>
        </CardContent>
      </Card>
    );
  }

  if (!testStarted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            {test.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{test.description}</p>
          
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Χρόνος: {test.time_limit_minutes} λεπτά</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">Ερωτήσεις: {test.questions.length}</span>
            </div>
          </div>

          <div className="border-l-4 border-primary pl-4">
            <h4 className="font-medium mb-2">Οδηγίες:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Διαβάστε προσεκτικά κάθε ερώτηση</li>
              <li>• Απαντήστε όσο πιο ειλικρινά μπορείτε</li>
              <li>• Χρησιμοποιήστε την κλίμακα 1-5 για τις απαντήσεις σας</li>
              <li>• Μπορείτε να επιστρέψετε σε προηγούμενες ερωτήσεις</li>
            </ul>
          </div>

          <div className="flex justify-between">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Ακύρωση
              </Button>
            )}
            <Button onClick={startTest} className="ml-auto">
              Έναρξη Τεστ
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentQuestion = test.questions[currentQuestionIndex];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;

  // If no current question, don't render the test interface
  if (!currentQuestion) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-lg font-medium mb-2">Επεξεργασία...</div>
            <div className="text-muted-foreground">Παρακαλώ περιμένετε...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if current question is valid
  const isCurrentQuestionValid = isValidQuestion(currentQuestion);

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>
            Ερώτηση {currentQuestionIndex + 1} από {test.questions.length}
          </CardTitle>
          <div className="flex items-center gap-2 text-primary font-mono">
            <Clock className="w-4 h-4" />
            {formatTime(timeLeft)}
          </div>
        </div>
        <Progress value={getProgress()} className="mt-2" />
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Show error message for invalid questions */}
        {!isCurrentQuestionValid && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Πρόβλημα με την ερώτηση</span>
            </div>
            <p className="text-sm text-red-700 mt-2">
              Αυτή η ερώτηση δεν έχει έγκυρες επιλογές απάντησης ή δεν είναι διαθέσιμη διάταξη. 
              Μπορείτε να την παραλείψετε και να συνεχίσετε.
            </p>
          </div>
        )}

        {/* Special handling for memory questions */}
        {isMemoryQuestion(currentQuestion) && isCurrentQuestionValid ? (
          <div className="space-y-6">
            {memoryPhase === 'study' ? (
              <div className="text-center space-y-4">
                {currentMemorySequence === 'LAYOUT' ? (
                  // Show security checkpoint layout
                  <div>
                    <div className="text-xl font-bold text-primary mb-6">
                      Μελετήστε αυτή τη διάταξη σημείου ελέγχου ασφαλείας:
                    </div>
                    <div className="bg-gray-100 p-8 rounded-lg max-w-md mx-auto">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {/* Row A */}
                        <div className="text-center font-bold">A1</div>
                        <div className="text-center font-bold">A2</div>
                        <div className="text-center font-bold">A3</div>
                        
                        {/* Row A items */}
                        <div className="bg-blue-200 p-3 rounded text-center text-xs">
                          Ανιχνευτής<br/>Μετάλλων
                        </div>
                        <div className="bg-green-200 p-3 rounded text-center text-xs">
                          Μηχάνημα<br/>Ακτίνων Χ
                        </div>
                        <div className="bg-yellow-200 p-3 rounded text-center text-xs">
                          Γραφείο<br/>Φρουρού
                        </div>
                        
                        {/* Row B */}
                        <div className="text-center font-bold mt-4">B1</div>
                        <div className="text-center font-bold mt-4">B2</div>
                        <div className="text-center font-bold mt-4">B3</div>
                        
                        {/* Row B items */}
                        <div className="bg-red-200 p-3 rounded text-center text-xs">
                          Καρέκλα<br/>Επισκέπτη
                        </div>
                        <div className="bg-purple-200 p-3 rounded text-center text-xs font-bold">
                          Μηχάνημα<br/>Ακτίνων Χ
                        </div>
                        <div className="bg-orange-200 p-3 rounded text-center text-xs">
                          Έλεγχος<br/>Εισόδου
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Show number sequence
                  <div>
                    <div className="text-xl font-bold text-primary">
                      Απομνημονεύστε αυτή την ακολουθία:
                    </div>
                    <div className="text-3xl font-mono bg-muted p-6 rounded-lg tracking-widest">
                      {currentMemorySequence}
                    </div>
                  </div>
                )}
                <div className="text-sm text-muted-foreground">
                  Χρόνος: {10 - memoryTimer} δευτερόλεπτα
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${(memoryTimer / 9) * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-lg font-medium leading-relaxed">
                  {currentMemorySequence === 'LAYOUT' ? 
                    currentQuestion.text.replace('Μελετήστε αυτή τη διάταξη σημείου ελέγχου ασφαλείας για 10 δευτερόλεπτα, στη συνέχεια ', '') :
                    currentQuestion.text
                  }
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  {(currentQuestion as any).options?.map((option: string, index: number) => {
                    const isSelected = currentAnswer === index;
                    
                    return (
                      <button 
                        key={index}
                        type="button"
                        onClick={() => handleAnswer(currentQuestion.id, index)}
                        className={`
                          px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm
                          ${isSelected 
                            ? 'border-primary bg-primary text-primary-foreground shadow-md' 
                            : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-primary/5'
                          }
                          min-w-[120px] flex-1 max-w-[200px]
                        `}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : isCurrentQuestionValid ? (
          <div className="space-y-4">
            <div className="text-lg font-medium leading-relaxed">
              {currentQuestion.text}
            </div>
            
            <div className="flex flex-wrap gap-3 justify-center">
              {/* Show different options based on question type */}
              {(currentQuestion as any).options ? (
                // Use options from database for cognitive tests
                (currentQuestion as any).options.map((option: string, index: number) => {
                  const isSelected = currentAnswer === index;
                  
                  return (
                    <button 
                      key={index}
                      type="button"
                      onClick={() => handleAnswer(currentQuestion.id, index)}
                      className={`
                        px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm
                        ${isSelected 
                          ? 'border-primary bg-primary text-primary-foreground shadow-md' 
                          : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-primary/5'
                        }
                        min-w-[120px] flex-1 max-w-[200px]
                      `}
                    >
                      {option}
                    </button>
                  );
                })
              ) : (
                // Use Likert scale for personality tests
                defaultLikertOptions.map((option, index) => {
                  const isSelected = currentAnswer === (index + 1);
                  
                  return (
                    <button 
                      key={index}
                      type="button"
                      onClick={() => handleAnswer(currentQuestion.id, (index + 1))}
                      className={`
                        px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm
                        ${isSelected 
                          ? 'border-primary bg-primary text-primary-foreground shadow-md' 
                          : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-primary/5'
                        }
                        min-w-[120px] flex-1 max-w-[200px]
                      `}
                    >
                      {option}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ) : null}

        {/* Navigation buttons - hide during memory study phase */}
        {!(isMemoryQuestion(currentQuestion) && memoryPhase === 'study') && (
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={goToPreviousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              Προηγούμενη
            </Button>
            
            <div className="flex gap-2">
              {/* Show skip button for invalid questions */}
              {!isCurrentQuestionValid && (
                <Button
                  variant="outline"
                  onClick={handleSkipQuestion}
                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Παράλειψη
                </Button>
              )}
              
              <Button
                onClick={goToNextQuestion}
                disabled={(currentAnswer === undefined && isCurrentQuestionValid) || loading}
              >
                {loading 
                  ? "Αποθήκευση..." 
                  : currentQuestionIndex === test.questions.length - 1 
                    ? "Ολοκλήρωση" 
                    : "Επόμενη"
                }
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
